import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, MessageSquare, Plus, RefreshCw, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ReadOnlyNotice } from "@/components/viewAs/ViewAs";
import { ConnectionCard, PendingCard, fixMessage } from "@/components/connections/ConnectionCard";
import { ConnectPanel } from "@/components/connections/ConnectPanel";
import { KeyForm, CustomAppForm, CustomKeyForm } from "@/components/connections/KeyForm";
import { AmazonGuide, GoogleAdsCustomerForm, HiggsfieldApproval, McpPopupState, PipedreamLinkState, ShopifyApproval, ShopifyPartnerForm } from "@/components/connections/ConnectFlows";
import AddConnections from "@/components/connections/AddConnections";
import { SharingPanel } from "@/components/connections/SharingPanel";
import { StatusPill } from "@/components/connections/StatusPill";
import {
  addableChoices, askedFields, assertSameWorkspace, cardMeta, clearConnectionCallbackQuery, describeChecked, describeConnectionActionError, filterChoices,
  isSignInInProgress, isUncheckableKey, isWorking, knownConnectionError, pickerChoices, popularChoices, secureAuthorizationUrl, shortAgo, signInEndedMessage, stateMeta,
  tally,
} from "@/components/connections/connectionsModel";
import { onConnectionsChanged, openAgentChat } from "@/components/chat/agentChatBus";
import "@/components/connections/connections.css";

// The Connections page: what the agent can use right now, checked for real, and the ways to give it more.
// Everything it draws lives in components/connections; this file only loads, polls, and runs the flows.

export { describeChecked, describeConnectionActionError, stateMeta, cardMeta, isUncheckableKey, assertSameWorkspace, secureAuthorizationUrl, clearConnectionCallbackQuery };

const POLL_MS = 3000;
const MCP_POLL_MS = 2000;
const MCP_RETRY_SECONDS = 60;
// How long a sign-in is waited on before the page gives up (the person may have shut the window): as long as its link
// works on the agent's machine, 30 minutes. And how many checks in a row may fail before it does the same: one dropped
// request is not the end of a sign-in that is still going.
const MCP_WAIT_MS = 30 * 60 * 1000;
const MCP_SLOW_AFTER_MS = 5 * 60 * 1000;
const MCP_SLOW_POLL_MS = 10 * 1000;
const MCP_POLL_FAILURES = 3;
const MCP_FLOW_EXPIRED = knownConnectionError({ code: "mcp_flow_expired" }).message;
// A check that fails for good, so no retry is worth it: the agent no longer has the flow, the session may not ask, or
// the answer was for another workspace. Anything else (no network, a 502 from a busy agent, a 503) may pass.
const MCP_POLL_FINAL_CODES = new Set(["mcp_flow_expired", "tenant_payload_mismatch", "tenant_response_missing", "workspace_changed"]);
const mcpCheckFailedForGood = (cause) => MCP_POLL_FINAL_CODES.has(cause?.code) || cause?.status === 401 || cause?.status === 403;
// Returned by an operation that has sent the browser to a provider.
const NAVIGATED = "navigated";
const PICK = { kind: "pick" };
// The apps whose callback brings the whole tab back here (?connection=<app>&status=<how it ended>), by their names.
const CALLBACK_LABELS = { meta_ads: "Meta Ads", amazon_seller: "Amazon Seller Central", google_ads: "Google Ads", google_analytics: "Google Analytics", google_search_console: "Google Search Console", google_sheets: "Google Sheets", google_drive: "Google Drive", gmail: "Gmail", tiktok_ads: "TikTok Ads", facebook_pages: "Facebook Pages", pinterest: "Pinterest", canva: "Canva", dropbox: "Dropbox" };
const callbackLabel = (appKey) => CALLBACK_LABELS[appKey] || (appKey ? appKey[0].toUpperCase() + appKey.slice(1) : "Connection");
/** What a callback's status means for the person: a sentence to show as a notice, or as an error. */
export function callbackOutcome(appKey, status) {
  const label = callbackLabel(appKey);
  if (status === "denied") return { error: { title: `${label} was not connected`, message: "The sign-in was cancelled before it finished. Sign in again when you are ready." } };
  if (status === "failed" || status === "error") return { error: { title: `${label} did not connect`, message: "The sign-in did not finish. Try again in a moment; if it keeps failing, ask your agent or message X SCALE." } };
  if (status === "no_ad_accounts") return { notice: `${label} signed in, but that Facebook login reaches no ad account yet. Give it access to the brand's ad accounts in Meta Business Manager, then sign in again.` };
  return { notice: `${label} signed in. Your agent can use it now.` };
}

/** Opens a provider in a small window; falls back to a tab when popups are blocked. */
export function openPopup(url, name = "growthos-connect") {
  try {
    const popup = window.open(url, name, "popup,width=520,height=720,noopener");
    return popup || null;
  } catch {
    return null;
  }
}

export function ConnectionsPage({ api: apiOverride, navigateToAuthorization = (url) => window.location.assign(url), confirmRemove = (card) => window.confirm(`${card.kind === "api_key" ? "Remove" : "Disconnect"} ${card.label} from your agent? You can connect it again afterward.`), openWindow = openPopup, navigateTo = (path) => window.location.assign(path) } = {}) {
  const app = useApp();
  const api = apiOverride || app.api;
  const selectClient = app.selectClient;
  const { clientId, clientSlug, clientName, role } = app.activeClient || {};
  // Auth rechecks return a new object even for the same workspace. Keep that refresh from restarting effects
  // and discarding an unfinished connection.
  const activeClient = useMemo(() => ({ clientId, clientSlug, clientName, role }), [clientId, clientSlug, clientName, role]);
  const memberEmail = app.member?.email;
  const [payload, setPayload] = useState(null);
  const [agentName, setAgentName] = useState("");
  const [chatAvailable, setChatAvailable] = useState(false);
  // Whether the brand's chat answer is in (either way), so a Start again link knows where the sign-in goes.
  const [chatChecked, setChatChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addStep, setAddStep] = useState(PICK);
  const [query, setQuery] = useState("");
  const [shopifyStatus, setShopifyStatus] = useState("pending");
  // Cards the agent accepted itself (a key through Hermes, an approved sign-in) that the sweep has not written
  // yet. They stay on the page across reloads until the payload carries the app.
  const [optimistic, setOptimistic] = useState({});
  // The tools shared with the brand this one is hosted with (its host, or the profiles it hosts), or null.
  const [sharing, setSharing] = useState(null);
  const addPanelRef = useRef(null);
  const addHeadingRef = useRef(null);
  const actionErrorRef = useRef(null);
  const generation = useRef(0);
  const callbackConsumed = useRef(false);
  const mcpStarting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const slugRef = useRef(activeClient?.clientSlug);
  useEffect(() => { slugRef.current = activeClient?.clientSlug; }, [activeClient?.clientSlug]);
  const current = useCallback((slug) => Boolean(slug) && slugRef.current === slug, []);
  const agent = agentName || "your agent";

  useEffect(() => {
    if (!addOpen) return;
    // Shelf tiles can be far below this panel. Reveal each step, and focus the heading so a phone reads it.
    addHeadingRef.current?.focus({ preventScroll: true });
    addPanelRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
  }, [addOpen, addStep.kind]);

  useEffect(() => {
    if (!actionError) return;
    actionErrorRef.current?.focus({ preventScroll: true });
    actionErrorRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
  }, [actionError]);

  const load = useCallback(async ({ preserve = false } = {}) => {
    const mine = ++generation.current;
    if (!preserve) setLoading(true);
    setError("");
    try {
      const next = assertSameWorkspace(await api.getAgentConnections(), activeClient);
      if (mine === generation.current) setPayload(next);
    } catch (cause) {
      if (mine === generation.current) { setError(cause?.message || "Could not load connections."); if (!preserve) setPayload(null); }
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, [api, activeClient]);

  // The sharing panel is read once per workspace, again after a share, when a tool is linked or removed on this page,
  // and when the page is shown again; never on a poll that changes nothing: it reads both brands' pages. A read that
  // fails leaves the panel as it was (a brand with no partner has none).
  const loadSharing = useCallback(async () => {
    if (typeof api.getConnectionSharing !== "function") return;
    const slug = activeClient?.clientSlug;
    try {
      const next = assertSameWorkspace(await api.getConnectionSharing(), activeClient);
      if (current(slug)) setSharing(next);
    } catch {
      // The panel keeps what it has.
    }
  }, [api, activeClient, current]);

  useEffect(() => {
    setPayload(null); setOptimistic({}); setAddOpen(false); setAddStep(PICK); setQuery(""); setNotice(""); setActionError(null); setBusyKey(null); setAgentName(""); setChatAvailable(false); setChatChecked(false); setSharing(null);
    callbackConsumed.current = false;
    load();
    loadSharing();
    const slug = activeClient?.clientSlug;
    if (typeof api.getAgentProfile === "function") {
      api.getAgentProfile().then((profile) => { const checked = assertSameWorkspace(profile, activeClient); if (current(slug)) setAgentName(checked?.agent?.name || ""); }).catch(() => {});
    }
    if (typeof api.getAgentChat === "function") {
      api.getAgentChat().then((chat) => { if (current(slug)) setChatAvailable(Boolean(chat?.available)); }).catch(() => {}).finally(() => { if (current(slug)) setChatChecked(true); });
    } else {
      setChatChecked(true);
    }
    return () => { generation.current += 1; };
  }, [load, loadSharing, api, current, activeClient, memberEmail]);

  // Which apps this brand has linked: when that set changes (a sign-in finished, a card removed), the panel's own list
  // and its "on the other brand" marks change with it. The first answer for a workspace is the one read beside it.
  const linkedApps = useMemo(() => (payload ? [...new Set((payload.connections || []).filter((row) => row.status !== "not_connected").map((row) => row.app))].sort().join(",") : null), [payload]);
  const seenLinked = useRef(null);
  useEffect(() => {
    if (linkedApps === null) { seenLinked.current = null; return; }
    if (seenLinked.current !== null && seenLinked.current !== linkedApps) loadSharing();
    seenLinked.current = linkedApps;
  }, [linkedApps, loadSharing]);
  // A tool linked on the other brand's page (another tab, or a Sign in for it) shows when this page is looked at again.
  useEffect(() => {
    const shown = () => { if (document.visibilityState !== "hidden") loadSharing(); };
    document.addEventListener("visibilitychange", shown);
    return () => document.removeEventListener("visibilitychange", shown);
  }, [loadSharing]);

  const working = useMemo(() => Boolean(payload?.connections?.some((row) => isWorking(row.job)) || payload?.pending?.some((row) => isWorking(row.job))), [payload]);
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(() => { load({ preserve: true }); }, POLL_MS);
    return () => clearInterval(timer);
  }, [working, load]);
  // A progress notice ("Checking…", "Installing…") is only true while a job is running; the cards carry the result.
  const wasWorking = useRef(false);
  useEffect(() => {
    if (wasWorking.current && !working) setNotice((text) => (/^(Checking |Installing on your agent|Higgsfield approved)/.test(text) ? "" : text));
    wasWorking.current = working;
  }, [working]);

  // A provider's callback brought the tab back here (the Pipedream and Meta callbacks): the cards reload, and the status
  // says how the sign-in ended (callbackOutcome).
  useEffect(() => {
    if (callbackConsumed.current) return;
    const params = new URLSearchParams(window.location.search);
    const callbackAppKey = params.get("connection");
    if (!callbackAppKey) return;
    const callbackStatus = params.get("status");
    callbackConsumed.current = true;
    clearConnectionCallbackQuery();
    const slug = activeClient?.clientSlug;
    (async () => {
      try {
        if (!current(slug)) return;
        await load({ preserve: true });
        const outcome = callbackOutcome(callbackAppKey, callbackStatus);
        if (outcome.error) setActionError(outcome.error);
        else setNotice(outcome.notice);
      } catch (cause) { if (current(slug)) setActionError(describeConnectionActionError(cause)); }
    })();
  }, [api, activeClient, current, load]);

  const run = async (key, operation) => {
    const slug = activeClient?.clientSlug;
    setBusyKey(key); setActionError(null); setNotice("");
    try {
      // Reloading after we have started a navigation races it: the fetch is aborted on the way out and the
      // failure renders as a banner, which reads as though connecting failed.
      const outcome = await operation();
      if (outcome !== NAVIGATED && current(slug)) await load({ preserve: true });
    }
    catch (cause) { if (current(slug)) setActionError(describeConnectionActionError(cause)); }
    finally { if (current(slug)) setBusyKey(null); }
  };

  const closeAdd = () => { setAddOpen(false); setAddStep(PICK); };
  const showNow = (card) => setOptimistic((prev) => ({ ...prev, [card.app]: card }));
  // The bubble finished a sign-in or landed a key on the agent: the card shows now, the payload reloads behind it.
  useEffect(() => onConnectionsChanged((detail) => {
    if (!current(activeClient?.clientSlug)) return;
    if (detail?.app && detail?.kind) {
      showNow({ app: detail.app, label: detail.label || detail.app, kind: detail.kind, status: detail.kind === "mcp" ? "connected" : "not_verified", reason: null, lastCheckedAt: detail.kind === "mcp" ? new Date().toISOString() : null, envKeys: [], accountLabel: null, toolCount: Number.isFinite(detail.toolCount) ? detail.toolCount : null, canTest: true, canRemove: true, canReconnect: false, job: null });
    }
    load({ preserve: true });
  }), [activeClient, current, load]);
  const keyCard = (appId, label, fields) => ({ app: appId, label, kind: "api_key", status: "not_verified", reason: null, lastCheckedAt: null, envKeys: Object.keys(fields || {}), accountLabel: null, toolCount: null, canTest: true, canRemove: Boolean(payload?.canManage), canReconnect: false, job: null });
  // The HUB checks a key the moment the agent has written it; an answer that carries that check says how it went.
  const checkedSentence = (result) => {
    const status = result?.result?.status;
    // A connected key that carries a reason is one no automatic check exists for: it is on the agent, nothing was checked.
    if (status === "connected") return result.result.reason ? "" : "It passed its check.";
    if (status === "not_working") return `Its check did not pass: ${result.result.reason || "the provider refused it."}`;
    return status ? "" : "The next check confirms it.";
  };
  const installedNotice = (label, result) => `${label} key installed on ${agent}. ${result?.restartNeeded ? `${agent[0].toUpperCase() + agent.slice(1)} can use it from its next message; its tools need a quick restart to see it. ` : ""}${checkedSentence(result)}`.trim();
  const openAdd = () => { setActionError(null); setAddOpen(true); setAddStep(PICK); };
  const stepTo = (step) => { setActionError(null); setNotice(""); setAddOpen(true); setAddStep(step); };

  /* ---------- card actions ---------- */

  // Every card the connections worker keeps is checked by queueing a job it answers. Amazon is the exception,
  // still checked through its own connector route.
  // The card's kind goes along: an app can hold a key and the agent's own sign-in, and the check is of the card shown.
  const testCard = (card) => run(card.app, async () => {
    if (card.kind === "pipedream") { assertSameWorkspace(await api.testConnection(card.app), activeClient); return; }
    const result = assertSameWorkspace(await api.testAgentConnection(card.app, card.kind), activeClient);
    // The agent's own check of an app it signed in to is answered on the spot (jobId null): there is no job to wait
    // for, and the reload that follows carries the result.
    if (result?.jobId !== null) setNotice(`Checking ${card.label}…`);
  });
  const removeCard = (card) => { if (!confirmRemove(card)) return; const slug = activeClient.clientSlug; run(card.app, async () => {
    if (card.kind === "pipedream") assertSameWorkspace(await api.disconnectConnection(card.app), activeClient);
    else assertSameWorkspace(await api.removeAgentConnection(card.app, card.kind), activeClient);
    if (!current(slug)) return;
    setNotice(`${card.label} disconnected. You can connect a different account now.`);
    if (card.app === "amazon_seller") stepTo({ kind: "amazon_account", item: { key: "amazon_seller", operation: "connect" } });
  }); };

  /* ---------- the sign-ins with their own flow ---------- */

  const connectHiggsfield = () => run("higgsfield", async () => {
    const slug = activeClient?.clientSlug;
    const result = assertSameWorkspace(await api.startConnection("higgsfield"), activeClient);
    const url = secureAuthorizationUrl(result?.authorizationUrl);
    if (result?.launchMode !== "device" || !url) throw new Error("The connection service did not return a Higgsfield approval link.");
    if (!current(slug)) return;
    setAddOpen(true); setAddStep({ kind: "higgsfield", authorizationUrl: url, pollSeconds: Math.max(3, Number(result.pollIntervalSeconds) || 0) });
  });
  const connectShopify = () => run("shopify", async () => {
    const slug = activeClient?.clientSlug;
    const result = assertSameWorkspace(await api.startShopifyConnection(), activeClient);
    const url = secureAuthorizationUrl(result?.authorizationUrl);
    if (!url || !result?.sessionId || new URL(url).hostname !== result.store || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(result.store)) throw new Error("Shopify did not return a valid store approval link.");
    if (!current(slug)) return;
    setShopifyStatus("pending"); setAddOpen(true); setAddStep({ kind: "shopify", authorizationUrl: url, sessionId: result.sessionId, store: result.store });
  });

  // The sign-in being waited on, if any. Closing the box or the Add panel, or leaving the page, ends the wait.
  const mcpStep = addOpen && addStep.kind === "mcp" && addStep.status === "authorization_required" ? addStep : null;
  // The native sign-in: Hermes on the agent's machine runs the OAuth; the page opens the window and asks whether it finished.
  // A sign-in already being waited on for this app, or one still starting, is left alone: a second start would make the
  // agent answer 409 and the page would stop watching the first.
  const connectMcp = (choice) => {
    if (mcpStarting.current || mcpStep?.choice.app === choice.app) return undefined;
    mcpStarting.current = true;
    return run(choice.app, async () => {
      const slug = activeClient?.clientSlug;
      try {
        const result = assertSameWorkspace(await api.connectMcp(choice.app), activeClient);
        const url = secureAuthorizationUrl(result?.authorizationUrl);
        if (!result?.flowId || !url) throw new Error("The agent did not return a sign-in link.");
        // Nothing is opened for a page the person has left or a workspace they have switched away from.
        if (!mounted.current || !current(slug)) return NAVIGATED;
        openWindow(url);
        setAddOpen(true); setAddStep({ kind: "mcp", choice, flowId: result.flowId, authorizationUrl: url, status: "authorization_required", error: null, toolCount: null, startedAt: Date.now() });
      } catch (cause) {
        if (!isSignInInProgress(cause) || !mounted.current || !current(slug)) throw cause;
        setAddOpen(true); setAddStep({ kind: "mcp", choice, status: "conflict", retryAt: Date.now() + MCP_RETRY_SECONDS * 1000 });
      } finally {
        mcpStarting.current = false;
      }
      return NAVIGATED;
    });
  };

  // A Pipedream Connect link in a window; the cards refresh when it closes.
  const connectPipedream = (choice, { reconnect = false, body } = {}) => run(choice.app, async () => {
    const slug = activeClient?.clientSlug;
    const method = reconnect ? api.reconnectConnection : api.startConnection;
    const answer = body ? await method(choice.app, body) : await method(choice.app);
    const result = assertSameWorkspace(answer, activeClient);
    const url = secureAuthorizationUrl(result?.connectLinkUrl || result?.authorizationUrl);
    if (!url) throw new Error("The connection service did not return a secure authorization URL.");
    if (!current(slug)) return NAVIGATED;
    // Amazon and anything that answers with a plain authorization link still sign in where the browser goes.
    if (!result?.connectLinkUrl) { navigateToAuthorization(url); return NAVIGATED; }
    const popup = openWindow(url);
    setAddOpen(true); setAddStep({ kind: "pipedream", choice, url, popup });
    return NAVIGATED;
  });

  const reconnectCard = (card) => {
    // Meta signs in again with Facebook, which takes the whole tab: no chat beside it, whether or not the chat is on.
    if (card.app === "meta_ads") { startFromItem({ key: "meta_ads", operation: "reconnect" }); return undefined; }
    // With the chat on, signing in again is a connection session like every pick: the bubble prepares the sign-in (the
    // dashboard checks its link first), says what it does, and offers the other ways. Shopify that signs in through the
    // brand's own Shopify app keeps its approval panel on the page, with the chat beside it.
    if (chatOffered) {
      if (card.app === "shopify") { askAgentBeside({ app: "shopify", label: card.label, flow: "shopify_oauth" }); return connectShopify(); }
      askAgentToConnect({ app: card.app, label: card.label, flow: card.kind === "pipedream" ? "pipedream" : card.app === "higgsfield" ? "higgsfield" : "mcp" });
      return undefined;
    }
    if (card.app === "shopify") return connectShopify();
    if (card.app === "higgsfield") return connectHiggsfield();
    if (card.app === "amazon_seller") { stepTo({ kind: "amazon_account", item: { key: card.app, operation: "reconnect" } }); return undefined; }
    if (card.kind === "mcp") return connectMcp({ app: card.app, label: card.label, flow: "mcp" });
    if (card.kind === "pipedream") return connectPipedream({ app: card.app, label: card.label, flow: "pipedream" }, { reconnect: true });
    // A key that does not work for an app the agent can sign in to through the dashboard's link (Figma, Asana, Klaviyo):
    // the server offers Sign in again on its card, and that is the agent's own sign-in, not hidden behind the key.
    if (card.kind === "api_key") return connectMcp({ app: card.app, label: card.label, flow: "mcp" });
    return run(card.app, async () => {
      const result = assertSameWorkspace(await api.reconnectConnection(card.app), activeClient);
      const url = secureAuthorizationUrl(result?.authorizationUrl);
      if (!url) throw new Error("The connection service did not return a secure authorization URL.");
      navigateToAuthorization(url);
      return NAVIGATED;
    });
  };

  /* ---------- tools shared with the brand this one is hosted with ---------- */

  const activeName = activeClient?.clientName || "this brand";
  // One key copied by the server from the one brand's vault into the other's and onto its agent, checked at once. A key
  // that came here shows as a card now; one that went to the partner shows on the partner's page.
  const shareTool = (row) => run(`share:${row.key}`, async () => {
    const direction = row.direction === "from" ? { from: row.partnerSlug } : { to: row.partnerSlug };
    const result = assertSameWorkspace(await api.shareAgentConnection(row.app, direction), activeClient);
    const [from, to] = row.direction === "from" ? [row.partnerName, activeName] : [activeName, row.partnerName];
    if (row.direction === "from") showNow(keyCard(row.app, row.label, {}));
    setNotice(`${row.label} key copied from ${from} to ${to}. ${checkedSentence(result)}`.trim());
    loadSharing();
  });
  // A sign-in is made once, on the page of the brand that lacks it: here, the app's own sign-in as Add starts it; for
  // the partner, its Connections page with the app picked, which starts the sign-in once that page has loaded.
  const openPartnerPage = (slug, appId, { agentSignsIn = false } = {}) => {
    const path = `/connections?connect=${encodeURIComponent(appId)}&workspace=${encodeURIComponent(slug)}${agentSignsIn ? "&signin=agent" : ""}`;
    Promise.resolve().then(() => selectClient?.(slug)).then(() => navigateTo(path)).catch((cause) => { if (current(activeClient?.clientSlug)) setActionError(describeConnectionActionError(cause)); });
  };
  // An app the agent signs in to itself (Canva: a Pipedream link would leave an account no agent can use) is asked of
  // this brand's agent in the chat, on that way by name; without the chat there is no one to ask.
  const askAgentToSignIn = (appId, label) => {
    if (!chatOffered) {
      setActionError({ title: `${label} is signed in by your agent itself`, message: `Turn on chat with ${agent} for this brand, then ask it to sign in to ${label} in its own browser.` });
      return;
    }
    openAgentChat({ seed: { kind: "connect", app: appId, label, flow: "mcp", way: "agent_browser", fields: [], guide: null } });
  };
  // A tool linked on this page: from the sharing panel, the app's own way in, as its tile in Add starts it.
  const connectHere = (row) => {
    if (row.signsInOnAgent) { askAgentToSignIn(row.app, row.label); return; }
    const choice = choices.find((entry) => entry.app === row.app);
    if (choice) { pickChoice(choice); return; }
    if (row.app === "higgsfield") { connectHiggsfield(); return; }
    connectPipedream({ app: row.app, label: row.label, flow: "pipedream" });
  };
  const signInForTool = (row) => {
    if (row.direction === "to") { openPartnerPage(row.partnerSlug, row.app, { agentSignsIn: row.signsInOnAgent }); return; }
    connectHere(row);
  };

  const addKeyApp = (appId, fields, extra = {}) => run(`add:${appId}`, async () => {
    const result = assertSameWorkspace(await api.addAgentConnection(appId, fields, extra), activeClient);
    const entry = payload?.addable?.apps?.find((item) => item.app === appId);
    closeAdd();
    // The agent wrote the key itself: no job to wait on, the card shows now and the sweep confirms it.
    const guided = GUIDED_FORMS.includes(entry?.flow);
    if (result?.status === "done" && !result?.jobId) {
      showNow(keyCard(appId, entry?.label || appId, fields)); setNotice(installedNotice(entry?.label || appId, result));
      if (guided) tellAgentFormDone(appId, "installed");
      return;
    }
    setNotice(entry?.flow === "shopify_partner" ? "Submitted to X SCALE." : "Installing on your agent. This takes about a minute.");
    if (guided && result?.status === "submitted") tellAgentFormDone(appId, "submitted");
  });
  const addCustom = (envKey, value) => run("add:custom", async () => {
    const result = assertSameWorkspace(await api.addCustomAgentConnection(envKey, value), activeClient);
    closeAdd();
    if (result?.status === "done" && !result?.jobId) { showNow(keyCard(envKey.toLowerCase(), envKey, { [envKey]: value })); setNotice(installedNotice(envKey, result)); return; }
    setNotice("Installing on your agent. This takes about a minute.");
  });
  // Amazon, Google Ads and Meta's Facebook sign-in reach the provider through the connector routes, which send the
  // browser away; the provider's callback brings the tab back to this page with the sign-in's status.
  const startFromItem = (item, body) => run(`library:${item.key}`, async () => {
    const method = item.operation === "reconnect" ? api.reconnectConnection : api.startConnection;
    const result = assertSameWorkspace(body ? await method(item.key, body) : await method(item.key), activeClient);
    const url = secureAuthorizationUrl(result?.connectLinkUrl || result?.authorizationUrl);
    if (!url) throw new Error("The connection service did not return a secure authorization URL.");
    navigateToAuthorization(url);
    return NAVIGATED;
  });

  // Replace a key that is there: the catalog's own form when it lists the app, else the custom form with the key's name.
  const replaceKey = (card) => {
    const entry = (payload?.addable?.apps || []).find((item) => item.app === card.app);
    if (entry) {
      const app = entry.flow === "shopify_oauth" ? { ...entry, flow: "key" } : entry;
      if (chatOffered && GUIDED_FORMS.includes(app.flow)) askAgentBeside({ app: app.app, label: app.label, flow: app.flow });
      stepTo({ kind: "form", app });
      return;
    }
    stepTo({ kind: "custom", envKey: card.envKeys?.[0] || "" });
  };

  // Hand the app to the chat bubble: the server starts the session on the best way to connect it (it mints the sign-in,
  // or hands over the key fields) and lists the other ways, the agent walks the person through it and checks the result.
  // The bubble brings forward a session it already has open for the app rather than starting another.
  const askAgentToConnect = (choice) => {
    closeAdd();
    openAgentChat({ seed: { kind: "connect", app: choice.app, label: choice.label, flow: choice.flow || choice.entry?.flow || "key", fields: choice.entry?.fields || [], guide: choice.entry?.guide || null } });
  };
  // The guided forms (Shopify's Partner request or its own sign-in) stay on the page, and with the chat on the bubble
  // opens beside them on a session that walks the person through the form (method "guided").
  const askAgentBeside = (choice) => {
    openAgentChat({ seed: { kind: "connect", app: choice.app, label: choice.label, flow: choice.flow, fields: [], guide: null } });
  };
  // The bubble's session for a guided form ends with the form: the agent is told it is done and checks. It reads whether
  // the chat is offered from a ref, so the Shopify poll below can call it without restarting on every render.
  const chatOfferedRef = useRef(false);
  const tellAgentFormDone = useCallback((app, outcome) => { if (chatOfferedRef.current) openAgentChat({ finished: { app, outcome } }); }, []);
  const askAgent = () => openAgentChat({ draft: "I want to connect " });
  // A seeded session is for owners and admins of a brand whose chat is switched on, for every app: keys, native
  // sign-ins, Pipedream links, Higgsfield in the bubble, and the guided Shopify forms on the page beside it. Meta's
  // Facebook sign-in takes the whole tab, so the chat is never opened for it from here.
  const chatOffered = chatAvailable && Boolean(payload?.canManage);
  chatOfferedRef.current = chatOffered;
  const GUIDED_FORMS = ["shopify_partner", "shopify_oauth"];
  const GUIDED_FLOWS = [...GUIDED_FORMS, "meta_signin", "google_ads_customer"];
  const agentTakes = (flow) => Boolean(flow) && !GUIDED_FLOWS.includes(flow);
  const canAskAbout = (entry) => {
    const catalogFlow = (payload?.addable?.apps || []).find((item) => item.app === entry?.app)?.flow || entry?.flow || "key";
    return chatOffered && agentTakes(catalogFlow);
  };
  // The hero's ask field: the sentence becomes the first message of the bubble.
  const askAgentWith = (message) => openAgentChat({ message });
  // A card that is not working, or an install that failed, is handed to the agent with what the dashboard knows.
  const fixWithAgent = (card) => openAgentChat({ message: fixMessage(card.label || card.app, { reason: card.job?.status === "failed" ? card.job.error : card.reason, failed: card.job?.status === "failed" }) });

  // Every row of the picker and every tile of the hero hand the app to the agent when chat is on. Without chat, or for
  // the guided forms, the page's own flows run: sign-in window, link window, key form.
  // Something else, with the agent: an app the Add panel lists is picked as itself, a name that matches some of them
  // shows those, and any other app opens the bubble on the agent's own playbook for it (a key field beside the chat
  // under a name the person can change, the app's own sign-in, the agent's browser).
  const askAboutApp = (name, appId) => {
    const wanted = name.trim().toLowerCase();
    const listed = choices.find((choice) => choice.app === appId || String(choice.label).toLowerCase() === wanted);
    if (listed) { pickChoice(listed); return; }
    if (filterChoices(pickerChoices(choices), name).length) { setQuery(name.trim()); setAddStep(PICK); return; }
    closeAdd();
    openAgentChat({ seed: { kind: "connect", app: appId, label: name.trim(), flow: "custom", fields: [], guide: null } });
  };

  const pickChoice = (choice) => {
    const flow = choice.flow || "key";
    if (chatOffered && agentTakes(flow)) { askAgentToConnect(choice); return; }
    // Meta Ads: Sign in with Facebook, the whole tab, back here through Meta's callback.
    if (flow === "meta_signin") { startFromItem({ key: choice.app, operation: "connect" }); return; }
    if (chatOffered && GUIDED_FORMS.includes(flow)) askAgentBeside({ app: choice.app, label: choice.label, flow });
    if (flow === "chat") { stepTo({ kind: "form", app: { ...choice.entry, flow: "key" } }); return; }
    if (flow === "mcp") { connectMcp(choice); return; }
    if (flow === "shopify_oauth") { connectShopify(); return; }
    if (flow === "higgsfield" || choice.app === "higgsfield") { connectHiggsfield(); return; }
    if (choice.app === "amazon_seller") { stepTo({ kind: "amazon_account", item: { key: "amazon_seller", operation: "connect" } }); return; }
    if (flow === "google_ads_customer") { stepTo({ kind: "google_ads_customer", item: { key: choice.app, operation: "connect" } }); return; }
    if (flow === "pipedream") { connectPipedream(choice); return; }
    stepTo({ kind: "form", app: choice.entry || choice });
  };

  /* ---------- polling the steps that wait ---------- */

  // While the sign-in window is open, ask every couple of seconds whether the person finished (every ten seconds after
  // the first five minutes): for as long as the link works, and through a failed check or two (three in a row end it).
  // Closing the box or the panel, a workspace switch (which closes the panel) or leaving the page stops asking.
  useEffect(() => {
    if (!mcpStep) return undefined;
    const slug = activeClient?.clientSlug;
    let stopped = false, timer = null, failed = 0;
    const poll = async () => {
      if (Date.now() - mcpStep.startedAt >= MCP_WAIT_MS) { setAddStep({ ...mcpStep, status: "error", error: MCP_FLOW_EXPIRED }); return; }
      try {
        const flow = assertSameWorkspace(await api.getMcpFlow(mcpStep.flowId), activeClient);
        if (stopped || !current(slug)) return;
        failed = 0;
        if (flow?.status === "approved") {
          const toolCount = Number.isFinite(flow.toolCount) ? flow.toolCount : null;
          setAddStep({ ...mcpStep, status: "approved", toolCount });
          // Show the card right away; the sweep's own row replaces it once it lands.
          showNow({ app: mcpStep.choice.app, label: mcpStep.choice.label, kind: "mcp", status: "connected", reason: null, lastCheckedAt: new Date().toISOString(), envKeys: [], accountLabel: null, toolCount, canTest: true, canRemove: true, canReconnect: false, job: null });
          setNotice(`${mcpStep.choice.label} signed in. ${agent[0].toUpperCase() + agent.slice(1)} can use it now.`);
          load({ preserve: true });
          return;
        }
        // How it ended, in the dashboard's words: never what the agent's machine or the provider said.
        if (flow?.status === "error") { setAddStep({ ...mcpStep, status: "error", error: signInEndedMessage(flow.outcome, mcpStep.choice.label) }); return; }
        timer = setTimeout(poll, pollDelay());
      } catch (cause) {
        if (stopped || !current(slug)) return;
        failed += 1;
        if (mcpCheckFailedForGood(cause) || failed >= MCP_POLL_FAILURES) setAddStep({ ...mcpStep, status: "error", error: MCP_FLOW_EXPIRED });
        else timer = setTimeout(poll, pollDelay());
      }
    };
    const pollDelay = () => (Date.now() - mcpStep.startedAt >= MCP_SLOW_AFTER_MS ? MCP_SLOW_POLL_MS : MCP_POLL_MS);
    timer = setTimeout(poll, MCP_POLL_MS);
    return () => { stopped = true; clearTimeout(timer); };
  }, [mcpStep, api, activeClient, current, load, agent]);

  // The 409 countdown.
  const [tick, setTick] = useState(0);
  const conflictStep = addOpen && addStep.kind === "mcp" && addStep.status === "conflict" ? addStep : null;
  useEffect(() => {
    if (!conflictStep) return undefined;
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [conflictStep]);
  const retryInSeconds = conflictStep ? Math.max(0, Math.ceil((conflictStep.retryAt - Date.now()) / 1000)) : 0;
  void tick;

  // The Pipedream window: when it closes, the cards reload.
  const pipedreamStep = addOpen && addStep.kind === "pipedream" ? addStep : null;
  useEffect(() => {
    if (!pipedreamStep?.popup) return undefined;
    const slug = activeClient?.clientSlug;
    const timer = setInterval(() => {
      let closed = false;
      try { closed = Boolean(pipedreamStep.popup.closed); } catch { closed = true; }
      if (!closed) return;
      clearInterval(timer);
      if (!current(slug)) return;
      closeAdd(); setNotice(`Checking the ${pipedreamStep.choice.label} link…`); load({ preserve: true });
    }, 500);
    return () => clearInterval(timer);
  }, [pipedreamStep, activeClient, current, load]);

  // While the approval panel is open, ask every few seconds whether the Higgsfield link was approved. Closing the
  // panel, a workspace switch (which closes it) or leaving the page stops asking.
  const higgsfieldStep = addOpen && addStep.kind === "higgsfield" && !addStep.expired ? addStep : null;
  useEffect(() => {
    if (!higgsfieldStep) return undefined;
    const slug = activeClient?.clientSlug;
    let stopped = false, timer = null;
    const poll = async () => {
      try {
        const result = assertSameWorkspace(await api.completeHiggsfieldConnection(), activeClient);
        if (stopped || !current(slug)) return;
        if (result?.status === "pending") { timer = setTimeout(poll, higgsfieldStep.pollSeconds * 1000); return; }
        if (result?.status !== "installing") throw new Error("The connection service returned an unexpected Higgsfield status.");
        // The answer comes once the agent holds the sign-in and the new token was checked; an older answer only queued it.
        closeAdd(); setNotice(result?.result ? "Higgsfield approved and installed on your agent." : "Higgsfield approved. Installing on your agent. This takes about a minute."); load({ preserve: true });
      } catch (cause) {
        if (stopped || !current(slug)) return;
        if (cause?.status === 410) setAddStep({ ...higgsfieldStep, expired: true });
        else { setAddStep(PICK); setActionError(describeConnectionActionError(cause)); }
      }
    };
    timer = setTimeout(poll, higgsfieldStep.pollSeconds * 1000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [higgsfieldStep, api, activeClient, current, load]);

  const shopifyStep = addOpen && addStep.kind === "shopify" ? addStep : null;
  useEffect(() => {
    if (!shopifyStep) return undefined;
    const slug = activeClient?.clientSlug;
    let stopped = false, timer;
    const poll = async () => {
      try {
        const result = assertSameWorkspace(await api.completeShopifyConnection(shopifyStep.sessionId), activeClient);
        if (stopped || !current(slug)) return;
        if (result.status === "connected") { closeAdd(); setNotice(`Shopify connected. Your agent can access ${shopifyStep.store}.`); load({ preserve: true }); tellAgentFormDone("shopify", "signed_in"); return; }
        if (!["pending", "installing"].includes(result.status)) throw new Error("Unexpected Shopify connection status.");
        setShopifyStatus(result.status);
        timer = setTimeout(poll, 3000);
      } catch (cause) {
        if (stopped || !current(slug)) return;
        setShopifyStatus("failed"); setActionError(describeConnectionActionError(cause)); load({ preserve: true });
      }
    };
    timer = setTimeout(poll, 3000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [shopifyStep, api, activeClient, current, load, tellAgentFormDone]);

  /* ---------- what the page shows ---------- */

  const cards = useMemo(() => {
    const own = (payload?.connections || []).filter((card) => card.status !== "not_connected");
    // A held card steps aside for the server's own row, and for a pending install of the same app.
    const seen = new Set([...own.map((card) => card.app), ...(payload?.pending || []).map((entry) => entry.app)]);
    // A held card can be removed only by someone who can manage the page, whatever it said when it was made.
    return [...own, ...Object.values(optimistic).filter((card) => !seen.has(card.app)).map((card) => ({ ...card, canRemove: Boolean(payload?.canManage) }))];
  }, [payload, optimistic]);
  useEffect(() => {
    const present = new Set((payload?.connections || []).map((card) => card.app));
    setOptimistic((prev) => { const next = Object.fromEntries(Object.entries(prev).filter(([app]) => !present.has(app))); return Object.keys(next).length === Object.keys(prev).length ? prev : next; });
  }, [payload]);
  const pending = useMemo(() => payload?.pending || [], [payload]);
  // Apps every brand has without connecting anything (Higgsfield, on GrowthOS's account, paid from Alloy credits).
  const included = useMemo(() => payload?.included || [], [payload]);
  const counts = useMemo(() => tally(cards), [cards]);
  const connectedKeys = useMemo(() => new Set(cards.map((card) => card.app)), [cards]);
  const pendingKeys = useMemo(() => new Set(pending.map((entry) => entry.app)), [pending]);
  const higgsfieldCard = cards.find((card) => card.app === "higgsfield");
  const guideByApp = useMemo(() => Object.fromEntries((payload?.addable?.apps || []).map((entry) => [entry.app, entry.guide])), [payload]);
  const shopifyOffered = Boolean(payload?.canManage) && (payload?.addable?.apps || []).some((entry) => entry.flow === "shopify_oauth");
  // Everything the picker and the shelf can offer. The two sign-ins with their own flow are offered even when the
  // catalog does not list them, as long as nothing with that name is on the agent.
  const choices = useMemo(() => {
    const includedApps = new Set(included.map((entry) => entry.app));
    const addable = (payload?.addable?.apps || []).filter((entry) => entry.flow !== "shopify_oauth" && !includedApps.has(entry.app));
    const listed = new Set(addable.map((entry) => entry.app));
    const specials = [];
    if (!listed.has("amazon_seller")) specials.push({ app: "amazon_seller", label: "Amazon Seller Central", group: "Commerce", flow: "pipedream", fields: [], guide: null });
    if (!listed.has("higgsfield") && !includedApps.has("higgsfield") && higgsfieldCard?.status !== "connected") specials.push({ app: "higgsfield", label: "Higgsfield", group: "Creative", flow: "higgsfield", fields: [], guide: null });
    const connected = new Set([...connectedKeys].filter((key) => key !== "higgsfield" || higgsfieldCard?.status === "connected"));
    return addableChoices({ addable: [...addable, ...specials], connected, pending: pendingKeys });
    // The picker and the shelf each take what they show from this one list (pickerChoices / popularChoices).
  }, [payload, included, connectedKeys, pendingKeys, higgsfieldCard]);
  const shelf = useMemo(() => (payload?.canManage ? popularChoices(choices, 6) : { shown: [], total: 0 }), [choices, payload?.canManage]);

  // Start again, from the HUB's page for a sign-in link that expired: /connections?connect=<app>&workspace=<slug> picks
  // the app again once the page and the chat answer are in, which starts a fresh sign-in (in the chat bubble when the
  // brand has one). A link for another workspace than the one open here does nothing.
  const startAgainConsumed = useRef(false);
  useEffect(() => {
    if (startAgainConsumed.current || !payload || !chatChecked) return;
    const params = new URLSearchParams(window.location.search);
    const app = params.get("connect");
    if (!app) return;
    startAgainConsumed.current = true;
    clearConnectionCallbackQuery();
    const workspace = params.get("workspace");
    if (workspace && workspace !== activeClient?.clientSlug) return;
    // The partner brand's creative-tools panel sent the person here to have this brand's agent sign in itself.
    if (params.get("signin") === "agent" && payload.canManage) { askAgentToSignIn(app, choices.find((entry) => entry.app === app)?.label || app); return; }
    const choice = payload.canManage ? choices.find((entry) => entry.app === app) : null;
    if (choice) pickChoice(choice);
    // pickChoice is a plain function of this render; the link is acted on once, with the render that has it all in.
  }, [payload, chatChecked, choices, activeClient]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !payload) return <div className="cx-loading"><Loader2 size={18} className="cx-spin" /> Loading connections…</div>;
  if (!payload) return <div className="cx-page"><div className="cx-notice cx-notice--bad"><AlertCircle size={17} /><div><strong>Connections unavailable</strong><p>{error || "Could not load connections."}</p></div><button type="button" onClick={() => load()}>Try again</button></div></div>;

  const stepApp = addStep.kind === "form" ? addStep.app : addStep.kind === "mcp" || addStep.kind === "pipedream" ? addStep.choice
    : addStep.kind === "higgsfield" ? { app: "higgsfield", label: "Higgsfield" } : addStep.kind === "shopify" ? { app: "shopify", label: "Shopify" }
    : addStep.kind === "amazon_account" ? { app: "amazon_seller", label: "Amazon Seller Central" } : addStep.kind === "google_ads_customer" ? { app: "google_ads", label: "Google Ads" } : null;
  const stepTitle = addStep.kind === "custom" ? "Something else" : stepApp ? `Connect ${stepApp.label}` : null;
  const empty = !cards.length && !pending.length;

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <span className="cx-head__eyebrow">Connections</span>
          <h1 className="cx-head__title">Connected to {agent}</h1>
          <ReadOnlyNotice className="mt-2" />
          <p className="cx-head__line">
            {counts.total ? <StatusPill tone="good">{counts.connected} connected</StatusPill> : null}
            {counts.problems ? <StatusPill tone="bad">{counts.problems} {counts.problems === 1 ? "needs" : "need"} attention</StatusPill> : null}
            {counts.saved ? <StatusPill tone="quiet">{counts.saved} saved</StatusPill> : null}
            <span>{payload.lastSweepAt ? `Checked for real, last full check ${shortAgo(payload.lastSweepAt)}.` : "Everything your agent can use right now."}</span>
          </p>
        </div>
        <div className="cx-head__actions">
          <button type="button" className="cx-btn" data-testid="connections-refresh" onClick={() => load({ preserve: true })}><RefreshCw size={14} />Refresh</button>
          {payload.canManage ? <button type="button" className="cx-btn cx-btn--primary" data-testid="agent-connections-add" onClick={openAdd}><Plus size={14} />Add connection</button> : null}
        </div>
      </header>

      {!payload.checksAvailable ? <div className="cx-notice cx-notice--warn"><AlertCircle size={17} /><p>Checks are unavailable right now. Cards show their last known state.</p></div> : null}
      {notice ? <div className="cx-notice cx-notice--good" role="status"><Check size={17} /><p>{notice}</p></div> : null}
      {actionError ? <div ref={actionErrorRef} tabIndex={-1} role="alert" className="cx-notice cx-notice--bad"><AlertCircle size={17} /><div><strong>{actionError.title}</strong><p>{actionError.message}</p></div></div> : null}
      {error ? <div className="cx-notice cx-notice--warn"><AlertCircle size={17} /><p>{error} The last verified state remains visible.</p></div> : null}

      {payload.canManage && !addOpen ? <AddConnections choices={shelf.shown} total={shelf.total} agentName={agent} busyKey={busyKey} chatAvailable={chatAvailable} onChoose={pickChoice} onAsk={askAgentWith} onSeeAll={openAdd} onCustom={() => stepTo({ kind: "custom" })} /> : null}

      {addOpen ? (
        <ConnectPanel
          step={addStep} panelRef={addPanelRef} headingRef={addHeadingRef} onClose={closeAdd} onBack={() => setAddStep(PICK)} stepTitle={stepTitle} stepApp={stepApp}
          choices={choices} query={query} onQuery={setQuery} onPick={pickChoice} onPickCustom={() => setAddStep({ kind: "custom", app: query.trim() })} busyKey={busyKey} agentName={agent}
          shopify={{ offered: shopifyOffered, onPick: () => { if (chatOffered) askAgentBeside({ app: "shopify", label: "Shopify", flow: "shopify_oauth" }); connectShopify(); } }}
        >
          {addStep.kind === "form" ? (
            addStep.app.flow === "shopify_partner" ? <ShopifyPartnerForm app={addStep.app} busy={busyKey === `add:${addStep.app.app}`} onSubmit={addKeyApp} onCancel={() => setAddStep(PICK)} />
            : <KeyForm app={addStep.app} busy={busyKey === `add:${addStep.app.app}`} onSubmit={addKeyApp} onCancel={() => setAddStep(PICK)} onAsk={canAskAbout(addStep.app) ? () => askAgentToConnect({ app: addStep.app.app, label: addStep.app.label, entry: addStep.app }) : null} />
          ) : null}
          {addStep.kind === "custom" && chatOffered && !addStep.envKey ? <CustomAppForm agentName={agent} initialName={addStep.app || ""} onAsk={askAboutApp} onCancel={() => setAddStep(PICK)} /> : null}
          {addStep.kind === "custom" && chatOffered && !addStep.envKey ? <p className="cx-flow__or" data-testid="agent-connection-custom-or">Already have the key's name and value? Add it here.</p> : null}
          {addStep.kind === "custom" ? <CustomKeyForm busy={busyKey === "add:custom"} pattern={payload.addable?.customKeyPattern} initialKey={addStep.envKey || ""} onSubmit={addCustom} onCancel={() => setAddStep(PICK)} /> : null}
          {addStep.kind === "amazon_account" ? <AmazonGuide clientName={activeClient.clientName} busy={busyKey === `library:${addStep.item.key}`} onContinue={() => startFromItem(addStep.item)} onCancel={closeAdd} /> : null}
          {addStep.kind === "google_ads_customer" ? <GoogleAdsCustomerForm busy={busyKey === `library:${addStep.item.key}`} onSubmit={(customerId) => startFromItem(addStep.item, { customerId })} onCancel={() => setAddStep(PICK)} /> : null}
          {addStep.kind === "higgsfield" ? <HiggsfieldApproval step={addStep} busy={busyKey === "higgsfield"} onRestart={connectHiggsfield} onCancel={openAdd} /> : null}
          {addStep.kind === "shopify" ? <ShopifyApproval step={addStep} status={shopifyStatus} busy={busyKey === "shopify"} onRestart={connectShopify} onBack={openAdd} /> : null}
          {addStep.kind === "mcp" ? (
            <McpPopupState choice={addStep.choice} flow={{ ...addStep, retryInSeconds: retryInSeconds }} busy={busyKey === addStep.choice.app}
              onOpenAgain={(event) => { event.preventDefault(); openWindow(addStep.authorizationUrl); }} onRetry={() => connectMcp(addStep.choice)} onCancel={() => setAddStep(PICK)} onDone={closeAdd}
              onUseKey={askedFields(addStep.choice.entry).length ? () => stepTo({ kind: "form", app: { ...addStep.choice.entry, flow: "key" } }) : null} />
          ) : null}
          {addStep.kind === "pipedream" ? (
            <PipedreamLinkState choice={addStep.choice} url={addStep.url} onOpenAgain={(event) => { event.preventDefault(); openWindow(addStep.url); }}
              onDone={() => { closeAdd(); setNotice(`Checking the ${addStep.choice.label} link…`); load({ preserve: true }); }} onCancel={() => setAddStep(PICK)} />
          ) : null}
        </ConnectPanel>
      ) : null}

      <SharingPanel sharing={sharing} activeName={activeName} busyKey={busyKey} canConnect={Boolean(payload.canManage)} onCopy={shareTool} onSignIn={signInForTool} onConnect={connectHere} />

      {included.length ? (
        <section className="cx-group" data-testid="connections-included" aria-label="Included with GrowthOS">
          <h2 className="cx-group__head">Included with GrowthOS <span className="cx-group__count">{included.length}</span></h2>
          <div className="cx-grid">
            {included.map((card) => <ConnectionCard key={`included-${card.app}`} card={card} agentName={agent} />)}
          </div>
        </section>
      ) : null}

      {!empty ? (
        <section className="cx-group" data-testid="connections-grid" aria-label="Connected">
          <h2 className="cx-group__head">Connected <span className="cx-group__count">{counts.total}</span></h2>
          <div className="cx-grid">
            {pending.map((entry) => <PendingCard key={`pending-${entry.app}`} entry={entry} guide={guideByApp[entry.app]} agentName={agent} onFix={chatAvailable ? fixWithAgent : undefined} />)}
            {cards.map((card) => <ConnectionCard key={card.app} card={card} canManage={payload.canManage} busy={busyKey === card.app} agentName={agent} canFix={chatAvailable} onTest={testCard} onRemove={removeCard} onReconnect={reconnectCard} onReplace={replaceKey} onFix={fixWithAgent} />)}
          </div>
        </section>
      ) : null}

      {empty ? (
        <div className="cx-empty" data-testid="connections-empty">
          <span className="cx-spark" aria-hidden="true"><Sparkles size={18} /></span>
          <h2 className="cx-empty__title">Nothing connected yet</h2>
          <p className="cx-empty__text">Give {agent} its first app. Ask it in chat, pick one below, or send a key in Slack.</p>
          {payload.canManage ? <div className="cx-form__actions">{chatAvailable ? <button type="button" className="cx-btn cx-btn--primary" onClick={askAgent}><MessageSquare size={14} />Ask {agent}</button> : null}<button type="button" className="cx-btn" onClick={openAdd}><Plus size={14} />Add a connection</button></div> : null}
        </div>
      ) : null}

    </div>
  );
}

export default ConnectionsPage;
